<div data-spartan-page class="p-6 md:p-8 space-y-6" style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page Header -->
  <div>
    <p class="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1">Shopee · Customs</p>
    <h1 class="text-2xl font-semibold tracking-tight text-foreground mb-1">Customs Clearance Progress</h1>
    <p class="text-sm text-muted-foreground mt-1">Submit customs clearance progress results to Shopee API</p>
  </div>

  <!-- Alerts -->
  @if (errorMessage) {
    <div class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-500/15 dark:border-red-500/30 p-3 text-sm text-red-800 dark:text-red-300">
      <svg class="size-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z"/></svg>
      <div class="flex-1">{{ errorMessage }}</div>
      <button class="appearance-none border-0 bg-transparent p-0 text-red-500 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 rounded" (click)="clearMessages()" aria-label="Close">
        <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>
  }
  @if (successMessage) {
    <div class="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 dark:bg-green-500/15 dark:border-green-500/30 p-3 text-sm text-green-800 dark:text-green-300">
      <svg class="size-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/></svg>
      <div class="flex-1">{{ successMessage }}</div>
      <button class="appearance-none border-0 bg-transparent p-0 text-green-500 hover:text-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400 rounded" (click)="clearMessages()" aria-label="Close">
        <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>
  }

  <!-- NOTE: JWT debug display (jwtDebugInfo / debugInfoVisible block) has been intentionally removed.
       Removed content included: full JWT token textarea with copy-to-clipboard, credentials table
       (Account / ClientName / SecretMasked / EndpointUsed), JWT Details table (Account / IssuedAt / ExpiresAt),
       decoded payload <pre> block, and auto-clear countdown. These exposed sensitive credentials on-screen.
       The TypeScript properties jwtDebugInfo, debugInfoVisible, and autoCloseTimer are retained in the .ts file
       but are no longer rendered. -->

  <!-- Customs Clearance Results Card -->
  <div class="rounded-lg border border-border bg-background">
    <div class="px-4 py-3 border-b border-border flex items-center justify-between">
      <h2 class="text-sm font-semibold text-foreground">Customs Clearance Results</h2>
      <button type="button"
        class="h-8 px-3 text-[13px] bg-accent text-accent-foreground border-0 rounded-md font-medium hover:bg-accent/90 transition-colors disabled:opacity-50"
        (click)="addEmptyResult()"
        [disabled]="isLoading">
        + Add Result
      </button>
    </div>
    <div class="p-4">
      <form (ngSubmit)="submitCustomsClearanceProgress()" #ccProgressForm="ngForm">

        @for (result of ccResults; track result; let i = $index) {
          <div class="mb-6">
            <div class="flex items-center justify-between mb-3">
              <p class="text-sm font-medium text-foreground">Result #{{ i + 1 }}</p>
              <button type="button"
                class="h-7 px-2.5 text-[13px] border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-300 rounded-md disabled:opacity-40 hover:bg-red-100 transition-colors"
                (click)="removeResult(i)"
                [disabled]="ccResults.length <= 1 || isLoading"
                title="Remove this result">
                Remove
              </button>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">

              <!-- Reference Number -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">
                  Reference No <span class="text-red-500">*</span>
                </label>
                <input type="text"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition disabled:opacity-60 disabled:cursor-not-allowed"
                  [(ngModel)]="result.reference_no"
                  [name]="'reference_no_' + i"
                  placeholder="e.g., SG2313123312"
                  required
                  [disabled]="isLoading" />
              </div>

              <!-- Clear Type -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">
                  Clear Type <span class="text-red-500">*</span>
                </label>
                <div class="relative">
                  <select
                    class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    [(ngModel)]="result.clear_type"
                    [name]="'clear_type_' + i"
                    required
                    [disabled]="isLoading">
                    <option value="">Select Clear Type</option>
                    <option [value]="1">1 - Import</option>
                    <option [value]="2">2 - Export</option>
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>

              <!-- Operation Time -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">
                  Operation Time <span class="text-red-500">*</span>
                </label>
                <input type="datetime-local"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition disabled:opacity-60 disabled:cursor-not-allowed"
                  [value]="getDateTimeValue(result.op_time)"
                  (change)="onOpTimeChange(i, $event)"
                  [name]="'op_time_' + i"
                  required
                  [disabled]="isLoading" />
              </div>

              <!-- Operation Location -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">
                  Operation Location <span class="text-red-500">*</span>
                </label>
                <input type="text"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition disabled:opacity-60 disabled:cursor-not-allowed"
                  [(ngModel)]="result.op_location"
                  [name]="'op_location_' + i"
                  placeholder="e.g., Indonesia"
                  required
                  [disabled]="isLoading" />
              </div>

              <!-- Event Code -->
              <div class="sm:col-span-2">
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">
                  Event Code <span class="text-red-500">*</span>
                </label>
                <div class="relative">
                  <select
                    class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    [(ngModel)]="result.event_code"
                    [name]="'event_code_' + i"
                    required
                    [disabled]="isLoading">
                    <option value="">Select Event Code</option>
                    <option value="I02">I02 - Import Custom Held</option>
                    <option value="I01">I01 - Import Custom Clearance Success</option>
                    <option value="E04">E04 - Delay exception (can be uploaded multiple times; must have reason code)</option>
                    <option value="H04">H04 - Handover to domestic LM</option>
                    <option value="I07">I07 - Update tax information only</option>
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>
            </div>

            @if (i < ccResults.length - 1) {
              <hr class="my-5 border-border" />
            }
          </div>
        }

        <!-- Action Buttons -->
        <div class="flex items-center gap-2 pt-2">
          <button type="submit"
            class="h-9 px-4 text-sm bg-accent text-accent-foreground border-0 rounded-md font-medium hover:bg-accent/90 transition-colors disabled:opacity-50"
            [disabled]="isLoading || !ccProgressForm.valid">
            @if (isLoading) {
              <span class="inline-block h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin mr-1.5 align-middle"></span>
            }
            {{ isLoading ? 'Processing...' : 'Submit Customs Clearance Progress' }}
          </button>
          <button type="button"
            class="h-9 px-4 text-sm border border-border-strong bg-surface text-foreground rounded-md hover:bg-surface-raised transition-colors disabled:opacity-50"
            (click)="resetForm()"
            [disabled]="isLoading">
            Reset Form
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Information Card -->
  <div class="rounded-lg border border-border bg-background">
    <div class="px-4 py-3 border-b border-border">
      <h2 class="text-sm font-semibold text-foreground">Customs Clearance Progress Information</h2>
    </div>
    <div class="p-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <p class="font-medium text-sm text-foreground mb-1">What is Customs Clearance Progress?</p>
          <p class="text-sm text-muted-foreground">
            Customs clearance progress tracking allows you to update Shopee with the current status
            of items going through customs clearance. You only need to provide the 5 essential fields.
          </p>
        </div>
        <div>
          <p class="font-medium text-sm text-foreground mb-2">Required Fields (5 Total)</p>
          <ul class="text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li><strong class="text-foreground">Reference No:</strong> The unique order identifier (maps to Shopee Order ID)</li>
            <li><strong class="text-foreground">Clear Type:</strong> Import (1) or Export (2)</li>
            <li><strong class="text-foreground">Operation Time:</strong> When the customs operation occurred</li>
            <li><strong class="text-foreground">Operation Location:</strong> Where the customs clearance is taking place</li>
            <li><strong class="text-foreground">Event Code:</strong> Select from predefined customs event codes:
              <ul class="mt-1 ml-4 list-disc">
                <li><code class="font-mono text-xs bg-surface-raised px-1 rounded">I02</code> - Import Custom Held</li>
                <li><code class="font-mono text-xs bg-surface-raised px-1 rounded">I01</code> - Import Custom Clearance Success</li>
                <li><code class="font-mono text-xs bg-surface-raised px-1 rounded">E04</code> - Delay exception (requires reason code)</li>
                <li><code class="font-mono text-xs bg-surface-raised px-1 rounded">H04</code> - Handover to domestic LM</li>
                <li><code class="font-mono text-xs bg-surface-raised px-1 rounded">I07</code> - Update tax information only</li>
              </ul>
            </li>
          </ul>
          <div class="flex items-start gap-2 rounded-lg border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/15 p-2 mt-3 text-xs text-blue-700 dark:text-blue-300">
            <svg class="size-3.5 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0zm-9-3.75h.008v.008H12V8.25z"/></svg>
            <span><strong>Simplified:</strong> All fields are now predefined selections for accuracy and consistency.</span>
          </div>
        </div>
      </div>
    </div>
  </div>

</div>
